import getResolution from '@/utils/answerUtils/getResolution';
import { v4 as uuidv4 } from 'uuid';

export enum MessageModelType {
  Heartbeat = 'Heartbeat',
  Statement = 'Statement',
  Question = 'Question',
  AnswerView = 'AnswerView',
  History = 'History',
  GoodBye = 'GoodBye',
  Error = 'Error',
  WebhookExecuted = 'WebhookExecuted',
  ScreenOut = 'ScreenOut',
  LanguageSwitched = 'LanguageSwitched',
  NoResponse = 'NoResponse',
  Media = 'Media',
  MatrixQuestion = 'MatrixQuestion',
  MatrixQuestionAnswered = 'MatrixQuestionAnswered',
  ModulesProgress = 'ModulesProgress'
}

export type MessageModel =
  | HeartbeatMessageModel
  | StatementMessageModel
  | QuestionMessageModel
  | MatrixQuestionMessageModel
  | MatrixQuestionAnsweredMessageModel
  | HistoryMessageModel
  | GoodByeMessageModel
  | ErrorMessageModel
  | WebhookExecutedMessageModel
  | LanguageSwitchedMessageModel
  | ScreenOutMessageModel
  | AnswerViewMessageModel
  | MediaMessageModel
  | ModulesProgressMessageModel;

export type StoreMessageModel = StatementMessageModel | QuestionMessageModel | MatrixQuestionMessageModel | GoodByeMessageModel;

export interface MessageModelBase {
  id: string;
  created_at: string; // ISO-8601 date strings
  kind: MessageModelType;
}

export interface ActiveMessage {
  message: MessageModel;
  shownAt: number;
  answers?: ActiveMessageAnswers;
  isMediaUnlocked?: boolean; // For Media messages to track if user can proceed
}

export type ActiveMessageAnswers = Answers | MatrixAnswers;

// HEARTBEAT

export interface HeartbeatMessageModel extends MessageModelBase {
  readonly kind: MessageModelType.Heartbeat;
}

// STATEMENT

export interface StatementMessageModel extends MessageModelBase {
  readonly kind: MessageModelType.Statement;
  name_html: string;
  display_delay?: number | null;
}

// MEDIA

export type MediaType = 'video' | 'image';

export interface MediaContent {
  type: MediaType;
  url: string;
}

export interface MediaMessageModel extends MessageModelBase {
  readonly kind: MessageModelType.Media;
  name_html: string;
  media_content: MediaContent;
  media_id: string;
  display_delay: number;
}

// QUESTION

export enum QuestionMessageModelType {
  RadioQuestion = 'RadioQuestion',
  MultipleQuestion = 'MultipleQuestion',
  LanguageQuestion = 'LanguageQuestion',
  OpenEndedQuestion = 'OpenEndedQuestion',
  RankOrderQuestion = 'RankOrderQuestion',
  SliderQuestion = 'SliderQuestion'
}

export type QuestionMessageModelDisplayType = 'auto' | 'row' | 'column';

/** Anchor labels for the slider scale; plain text from Dash (whitespace-only counts as empty). */
export interface SliderScaleLabels {
  start: string;
  middle: string;
  end: string;
}

/** Optional icon for each scale anchor (e.g. thumbs down / neutral / thumbs up). */
export interface SliderScaleImages {
  start?: QuestionOptionImageData | null;
  middle?: QuestionOptionImageData | null;
  end?: QuestionOptionImageData | null;
}

export interface SliderScaleConfig {
  min: number;
  max: number;
  increment: number;
  labels: SliderScaleLabels;
  images?: SliderScaleImages;
  /** N/A checkbox label HTML (`MessageHtml`). Falsy → no N/A row. */
  na_label?: string | null;
  /** Initial thumb on nearest tick to range midpoint; scale fields unchanged. */
  center_slider: boolean;
}

/** Present when `question_type` is `OpenEndedQuestion`. */
export interface OpenEndedConfig {
  display_as_series: boolean;
}

export interface QuestionMessageModel extends MessageModelBase {
  readonly kind: MessageModelType.Question;
  name_html: string;
  instructions_html?: string | null;
  question_id: string;
  question_type: QuestionMessageModelType;
  display_type: QuestionMessageModelDisplayType;
  question_options: QuestionOptionModel[];
  bundle_name_html?: string | null;
  display_delay?: number | null;
  image?: QuestionOptionImageData;
  /** Present when `question_type` is `SliderQuestion` (required for valid slider payloads). */
  scale_config?: SliderScaleConfig;
  /** Present when `question_type` is `OpenEndedQuestion`. */
  open_ended_config?: OpenEndedConfig;
}

/** Slider questions always include `scale_config` when valid. */
export type SliderQuestionMessageModel = QuestionMessageModel & {
  question_type: QuestionMessageModelType.SliderQuestion;
  scale_config: SliderScaleConfig;
};

export function isSliderQuestionMessage(m: QuestionMessageModel): m is SliderQuestionMessageModel {
  return m.question_type === QuestionMessageModelType.SliderQuestion && m.scale_config != null;
}

export function isOpenEndedSeriesQuestion(m: QuestionMessageModel): boolean {
  return m.question_type === QuestionMessageModelType.OpenEndedQuestion && m.open_ended_config?.display_as_series === true && m.question_options.length > 1;
}

// MATRIX QUESTION

export interface MatrixQuestionItem {
  question_id: string;
  name_html: string;
  image?: QuestionOptionImageData;
}

export type MatrixQuestionOptionModel = Omit<QuestionOptionModel, 'open_ended' | 'open_text_validation'> & {
  open_ended: false;
  open_text_validation?: never;
};

export interface MatrixQuestionMessageModel extends MessageModelBase {
  readonly kind: MessageModelType.MatrixQuestion;
  bundle_name_html: string;
  instructions_html?: string | null;
  bundle_id: string;
  question_type: QuestionMessageModelType.RadioQuestion;
  question_options: MatrixQuestionOptionModel[];
  questions: MatrixQuestionItem[];
  display_delay?: number | null;
}

export interface QuestionOptionModel {
  id: string;
  name_html: string;
  nota: boolean;
  open_ended: boolean;
  code?: string; // should be LanguageCode when implemented localisation;
  image?: QuestionOptionImageData;
  /** Length regex from the API, e.g. `"^[\\s\\S]{0,1500}$"`; used for OpenEndedQuestion bounds and submit validation. */
  open_text_validation?: string;
}

// ANSWER VIEW

export enum AnswerContext {
  Standalone = 'standalone',
  Bundle = 'bundle',
  Matrix = 'matrix'
}

export interface AnswerViewMessageModel extends MessageModelBase {
  readonly kind: MessageModelType.AnswerView;
  question_id: string;
  question_type: QuestionMessageModelType;
  answers: AnswerViewMessageAnswersModel[];
  answer_context?: AnswerContext;
}

export interface AnswerViewMessageAnswersModel {
  id: string;
  name_html: string;
  value?: string;
  image?: QuestionOptionImageData;
}

// MATRIX QUESTION ANSWERED

export interface MatrixQuestionAnsweredAnswerModel {
  question_id: string;
  name_html: string;
  answers: AnswerViewMessageAnswersModel[];
}

export interface MatrixQuestionAnsweredMessageModel extends MessageModelBase {
  readonly kind: MessageModelType.MatrixQuestionAnswered;
  bundle_id: string;
  question_type: QuestionMessageModelType.RadioQuestion;
  answers: MatrixQuestionAnsweredAnswerModel[];
}

// LANGUAGE SWITCHED

export interface LanguageSwitchedMessageModel extends MessageModelBase {
  readonly kind: MessageModelType.LanguageSwitched;
  language: string; // should be LanguageCode when implemented localisation;
}

// GOODBYE

export interface GoodByeMessageModel extends MessageModelBase {
  readonly kind: MessageModelType.GoodBye;
  message: string;
  redirect_url?: string;
}

export interface WebhookExecutedMessageModel extends MessageModelBase {
  readonly kind: MessageModelType.WebhookExecuted;
  name: string;
  reason: string;
}

export interface ScreenOutMessageModel extends MessageModelBase {
  readonly kind: MessageModelType.ScreenOut;
  name: string;
}

export interface ModulesProgressMessageModel extends MessageModelBase {
  readonly kind: MessageModelType.ModulesProgress;
  /** Completed module id. */
  survey_id: string;
  header_html: string;
  /** BE-provided localised progress caption (rendered as sanitized HTML). */
  progress_html: string;
  body_html: string;
  total_sections: number;
  completed_sections: number;
}

export interface QuestionOptionImageData {
  size: { width: number; height: number };
  url: string;
}

// ERROR

export enum ErrorModelType {
  InvalidMessage = 'InvalidMessage',
  Misunderstood = 'Misunderstood',
  SurveyNotActive = 'SurveyNotActive',
  UserMetadataMismatch = 'UserMetadataMismatch',
  InternalError = 'InternalError'
}

export interface ErrorMessageModel extends MessageModelBase {
  readonly kind: MessageModelType.Error;
  error: ErrorModel;
}

export interface ErrorModel {
  messages: string[];
  kind: ErrorModelType;
  info: Map<string, string[]> | null;
}

// SENTRY ERRORS

export class InvalidMessageError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = 'InvalidMessageError';
  }
}
export class MisunderstoodError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = 'MisunderstoodError';
  }
}
export class SurveyNotActiveError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = 'SurveyNotActiveError';
  }
}
export class UserMetadataMismatchError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = 'UserMetadataMismatchError';
  }
}
export class InternalErrorError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = 'InternalErrorError';
  }
}

export const sentryErrorClassByKind: Record<ErrorModelType, new (msg: string) => Error> = {
  [ErrorModelType.InvalidMessage]: InvalidMessageError,
  [ErrorModelType.Misunderstood]: MisunderstoodError,
  [ErrorModelType.SurveyNotActive]: SurveyNotActiveError,
  [ErrorModelType.UserMetadataMismatch]: UserMetadataMismatchError,
  [ErrorModelType.InternalError]: InternalErrorError
};

// HISTORY

export type HistoryMessages =
  | StatementMessageModel
  | QuestionMessageModel
  | MatrixQuestionMessageModel
  | MatrixQuestionAnsweredMessageModel
  | LanguageSwitchedMessageModel
  | AnswerViewMessageModel
  | MediaMessageModel
  | ModulesProgressMessageModel;

export interface HistoryMessageModel extends MessageModelBase {
  readonly kind: MessageModelType.History;
  messages: HistoryMessages[];
  size: number;
}

// ANSWERS

export interface Answers {
  questionId: string;
  questionType: QuestionMessageModelType;
  answer: AnswerValue[];
  /**
   * OpenEndedQuestion with multiple `question_options`: which option rows are shown.
   * Submit validation only applies to these ids. Omitted for single-option questions.
   */
  openEndedVisibleOptionIds?: string[];
}

export interface RankOrderAnswerValue {
  id: string;
  /** 1-based position; null = not ranked yet */
  rank: number | null;
}

export type AnswerValue = RadioAnswerValue | MultipleAnswerValue | OpenEndedAnswerValue | LanguageAnswerValue | RankOrderAnswerValue | SliderAnswerValue;

export interface RadioAnswerValue {
  id: string;
  text?: string;
}

export interface MultipleAnswerValue {
  id: string;
  selected: boolean;
  text?: string;
}

export interface OpenEndedAnswerValue {
  id: string;
  text: string;
}

export interface LanguageAnswerValue {
  id: string;
  code: string;
  text?: string;
}

export interface SliderAnswerValue {
  id: string;
  value: number | null;
  /** Until the user commits a slider value or N/A. */
  pending: boolean;
}

export interface MatrixAnswers {
  bundleId: string;
  questionType: QuestionMessageModelType.RadioQuestion;
  answer: MatrixAnswerValue[];
}

export function isMatrixAnswers(answers?: ActiveMessageAnswers | null): answers is MatrixAnswers {
  return Boolean(answers && 'bundleId' in answers);
}

export function hasUnsupportedMatrixOpenEndedOption(matrixMessage: MatrixQuestionMessageModel): boolean {
  return matrixMessage.question_options.some((option) => option.open_ended);
}

export interface MatrixAnswerValue {
  questionId: string;
  answer: RadioAnswerValue[];
}

/** Outgoing / wire `AnswersModel` value when the respondent chose N/A on a slider row. */
export enum SliderQuestionWireAnswer {
  NotApplicable = '999'
}

// ------------------------------------------------
// NEO OUTGOING
// ------------------------------------------------

export enum NeoMessageModelType {
  Answer = 'Answer',
  MatrixAnswers = 'MatrixAnswers',
  ConfirmMedia = 'ConfirmMedia',
  ConfirmNextModule = 'ConfirmNextModule',
  ResetConversation = 'ResetConversation',
  RollbackConversation = 'RollbackConversation'
}
export interface NeoMessageModelBase {
  id: string;
  created_at: string; // ISO-8601 date strings
  kind: NeoMessageModelType;
}

// ANSWERS

export interface AnswerNeoMessageModel extends NeoMessageModelBase {
  question_id: string;
  /** Option id -> value (selection marker, open text, or rank as string). */
  answers: AnswersModel;
  meta: Meta;
}

export interface MatrixAnswersNeoMessageModel extends NeoMessageModelBase {
  readonly kind: NeoMessageModelType.MatrixAnswers;
  bundle_id: string;
  answers: MatrixQuestionAnswerModel[];
  meta: Meta;
}

export interface MatrixQuestionAnswerModel {
  question_id: string;
  answers: AnswersModel;
}

export interface AnswersModel {
  [optionId: string]: string;
}

export interface RollbackConversationMessage extends NeoMessageModelBase {
  message_id: string;
}

export interface ConfirmMediaNeoMessageModel extends NeoMessageModelBase {
  media_id: string;
}

export interface ConfirmNextModuleNeoMessageModel extends NeoMessageModelBase {
  readonly kind: NeoMessageModelType.ConfirmNextModule;
  /** Completed module id echoed from ModulesProgress. */
  survey_id: string;
}

export interface ResetConversationMessage extends NeoMessageModelBase {}

export interface Meta {
  answered_at: number;
  screen_resolution_height: number;
  screen_resolution_width: number;
  shown_at: number;
  window_resolution_height: number;
  window_resolution_width: number;
}

// ------------------------------------------------
// HELPERS OUTGOING
// ------------------------------------------------

export function toAnswerNeoMessageModel(answers: Answers, shownAt: number): AnswerNeoMessageModel {
  if (!answers) {
    console.error('toAnswerNeoMessageModel:: answers: %s, meta: %s', JSON.stringify(answers));
    throw new Error('toAnswerNeoMessageModel:: Missing answers!');
  }

  return {
    id: uuidv4(),
    created_at: new Date().toISOString(),
    kind: NeoMessageModelType.Answer,
    meta: toMeta(shownAt),
    question_id: answers.questionId,
    answers: extractAnswersModel(answers)
  };
}

export function toMatrixAnswersNeoMessageModel(matrixAnswers: MatrixAnswers, shownAt: number): MatrixAnswersNeoMessageModel {
  if (!matrixAnswers) {
    console.error('toMatrixAnswersNeoMessageModel:: matrixAnswers: %s, meta: %s', JSON.stringify(matrixAnswers));
    throw new Error('toMatrixAnswersNeoMessageModel:: Missing matrix answers!');
  }

  return {
    id: uuidv4(),
    created_at: new Date().toISOString(),
    kind: NeoMessageModelType.MatrixAnswers,
    meta: toMeta(shownAt),
    bundle_id: matrixAnswers.bundleId,
    answers: extractMatrixAnswersModel(matrixAnswers)
  };
}

export function toConfirmMediaNeoMessageModel(mediaId: string): ConfirmMediaNeoMessageModel {
  return {
    id: uuidv4(),
    created_at: new Date().toISOString(),
    kind: NeoMessageModelType.ConfirmMedia,
    media_id: mediaId
  };
}

export function toConfirmNextModuleNeoMessageModel(surveyId: string): ConfirmNextModuleNeoMessageModel {
  return {
    id: uuidv4(),
    created_at: new Date().toISOString(),
    kind: NeoMessageModelType.ConfirmNextModule,
    survey_id: surveyId
  };
}

export function toMeta(shownAt: number): Meta {
  const { screenResolutionWidth, screenResolutionHeight, windowResolutionWidth, windowResolutionHeight } = getResolution();

  return {
    shown_at: shownAt,
    answered_at: Date.now(),
    screen_resolution_width: screenResolutionWidth,
    screen_resolution_height: screenResolutionHeight,
    window_resolution_width: windowResolutionWidth,
    window_resolution_height: windowResolutionHeight
  };
}

export function extractAnswersModel(answers: Answers): AnswersModel {
  switch (answers.questionType) {
    case QuestionMessageModelType.MultipleQuestion: {
      const multipleAnswers = answers.answer as MultipleAnswerValue[];
      return multipleAnswers
        .filter((val) => val.selected === true)
        .reduce((acc, curr) => {
          acc[curr.id] = curr.text ? curr.text : '';
          return acc;
        }, {});
    }

    case QuestionMessageModelType.RadioQuestion: {
      const radioAnswers = answers.answer as RadioAnswerValue[];
      return radioAnswers.reduce((acc, curr) => {
        acc[curr.id] = curr.text ? curr.text : '';
        return acc;
      }, {});
    }

    case QuestionMessageModelType.LanguageQuestion: {
      const languageAnswers = answers.answer as LanguageAnswerValue[];
      return languageAnswers.reduce((acc, curr) => {
        acc[curr.id] = '';
        return acc;
      }, {});
    }

    case QuestionMessageModelType.OpenEndedQuestion: {
      const openEndedAnswers = answers.answer as OpenEndedAnswerValue[];
      return openEndedAnswers.reduce((acc, curr) => {
        acc[curr.id] = curr.text;
        return acc;
      }, {});
    }

    case QuestionMessageModelType.RankOrderQuestion: {
      const rankAnswers = answers.answer as RankOrderAnswerValue[];
      return rankAnswers.reduce<AnswersModel>((acc, curr) => {
        if (curr.rank === null) {
          throw new Error('extractAnswersModel:: RankOrderQuestion has unranked options');
        }
        acc[curr.id] = String(curr.rank);
        return acc;
      }, {});
    }

    case QuestionMessageModelType.SliderQuestion: {
      const sliderAnswers = answers.answer as SliderAnswerValue[];
      return sliderAnswers.reduce((acc, curr) => {
        if (curr.pending) acc[curr.id] = '';
        else if (curr.value !== null) acc[curr.id] = String(curr.value);
        else acc[curr.id] = SliderQuestionWireAnswer.NotApplicable;
        return acc;
      }, {} as AnswersModel);
    }

    default:
      throw new Error('Unsupported question type');
  }
}

export function extractMatrixAnswersModel(matrixAnswers: MatrixAnswers): MatrixQuestionAnswerModel[] {
  return matrixAnswers.answer.map((row) => {
    if (row.answer.length === 0) {
      throw new Error('extractMatrixAnswersModel:: MatrixQuestion row has no answer');
    }

    return {
      question_id: row.questionId,
      answers: extractAnswersModel({
        questionId: row.questionId,
        questionType: QuestionMessageModelType.RadioQuestion,
        answer: row.answer
      })
    };
  });
}

export function isSliderQuestionComplete(answers: Answers, questionMessage: SliderQuestionMessageModel): boolean {
  const list = answers.answer as SliderAnswerValue[] | undefined;
  if (!list || list.length !== questionMessage.question_options.length) {
    return false;
  }
  const byId = new Map(list.map((a) => [a.id, a]));
  for (const option of questionMessage.question_options) {
    const entry = byId.get(option.id);
    if (!entry || entry.pending) {
      return false;
    }
  }
  return true;
}

// SURVEY TESTING

export function generateRollbackConversationMessage(messageId: string): RollbackConversationMessage {
  return {
    id: uuidv4(),
    created_at: new Date().toISOString(),
    kind: NeoMessageModelType.RollbackConversation,
    message_id: messageId
  };
}

export function generateResetConversationMessage(): ResetConversationMessage {
  return {
    id: uuidv4(),
    created_at: new Date().toISOString(),
    kind: NeoMessageModelType.ResetConversation
  };
}
